import { doc, updateDoc } from "firebase/firestore";
import { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { db } from "../../utils/firebase";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import trackEvent from "../../utils/analytics";

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,800;0,9..144,900;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

    .cp-root { font-family: 'Plus Jakarta Sans', sans-serif; }
    .cp-zh   { font-family: 'Fraunces', Georgia, serif; }

    @keyframes cp-bd   { from{opacity:0} to{opacity:1} }
    @keyframes cp-card { from{opacity:0;transform:translateY(22px) scale(.96)} to{opacity:1;transform:translateY(0) scale(1)} }
    @keyframes cp-spin { to{transform:rotate(360deg)} }

    .cp-bd-anim   { animation: cp-bd   .2s both; }
    .cp-card-anim { animation: cp-card .32s cubic-bezier(.22,.68,0,1.2) both; }

    /* ── Input ── */
    .cp-input {
      width:100%; border:1.5px solid #e8e8e4;
      border-radius:11px; padding:11px 44px 11px 14px;
      font-size:14px; font-family:'Plus Jakarta Sans',sans-serif;
      color:#111; background:#fafaf9; outline:none;
      transition:border-color .15s, background .15s, box-shadow .15s;
    }
    .cp-input::placeholder { color:#c0c0ba; }
    .cp-input:focus {
      border-color:#6cbb23; background:#fff;
      box-shadow:0 0 0 3px rgba(108,187,35,.1);
    }
    .cp-input:hover:not(:focus) { border-color:#d0d0ca; }

    /* ── Toggle eye btn ── */
    .cp-eye {
      position:absolute; right:12px; top:50%; transform:translateY(-50%);
      background:none; border:none; cursor:pointer; padding:4px;
      color:#bbb; transition:color .15s; display:flex; align-items:center;
      z-index:2;
    }
    .cp-eye:hover { color:#6cbb23; }

    /* ── Strength bar ── */
    .cp-bar-track {
      height:4px; border-radius:99px; background:#f0f0ec; overflow:hidden; margin-top:8px;
    }
    .cp-bar-fill {
      height:100%; border-radius:99px;
      transition:width .3s, background .3s;
    }

    /* ── Submit ── */
    .cp-submit {
      width:100%; border:none; background:#111; color:#fff;
      border-radius:11px; padding:13px;
      font-family:'Plus Jakarta Sans',sans-serif; font-size:14.5px; font-weight:700;
      cursor:pointer; position:relative; overflow:hidden;
      transition:transform .18s, box-shadow .18s, background .18s;
    }
    .cp-submit::after {
      content:''; position:absolute; inset:0;
      background:#6cbb23; transform:translateY(102%);
      transition:transform .28s cubic-bezier(.22,.68,0,1.2);
    }
    .cp-submit:hover { transform:translateY(-2px); box-shadow:0 10px 30px rgba(0,0,0,.2); }
    .cp-submit:hover::after { transform:translateY(0); }
    .cp-submit span { position:relative; z-index:1; }
    .cp-submit:active { transform:scale(.97); }
    .cp-submit:disabled { background:#e8e8e4; color:#c0c0ba; cursor:not-allowed; box-shadow:none; transform:none; }
    .cp-submit:disabled::after { display:none; }

    /* ── Close ── */
    .cp-close {
      position:absolute; top:14px; right:14px;
      width:30px; height:30px; border-radius:8px;
      border:1.5px solid rgba(255,255,255,.25);
      background:rgba(255,255,255,.1);
      display:flex; align-items:center; justify-content:center;
      cursor:pointer; z-index:20;
      transition:background .15s, border-color .15s;
    }
    .cp-close:hover { background:rgba(255,255,255,.22); border-color:rgba(255,255,255,.4); }

    /* ── Cancel ── */
    .cp-cancel {
      display:block; width:100%; margin-top:10px;
      background:none; border:none; cursor:pointer;
      font-size:12.5px; color:#bbb;
      font-family:'Plus Jakarta Sans',sans-serif; font-weight:500; padding:6px 0;
      transition:color .15s;
    }
    .cp-cancel:hover { color:#888; }

    /* ── Header deco — all pointer-events:none ── */
    .cp-hbg {
      position:absolute; inset:0; pointer-events:none;
      background-image:
        linear-gradient(rgba(108,187,35,.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(108,187,35,.05) 1px, transparent 1px);
      background-size:32px 32px;
    }
    .cp-hglow {
      position:absolute; top:-40%; left:50%; transform:translateX(-50%);
      width:320px; height:200px; border-radius:50%; pointer-events:none;
      background:radial-gradient(ellipse, rgba(108,187,35,.15) 0%, transparent 70%);
    }
    .cp-cx {
      position:absolute; width:13px; height:13px; pointer-events:none;
    }
    .cp-cx::before,.cp-cx::after { content:''; position:absolute; background:rgba(255,255,255,.14); }
    .cp-cx::before { left:50%; top:0; bottom:0; width:1.5px; transform:translateX(-50%); }
    .cp-cx::after  { top:50%; left:0; right:0; height:1.5px; transform:translateY(-50%); }

    /* ── Label ── */
    .cp-lbl {
      display:block; font-size:10px; font-weight:700; color:#b0b0aa;
      letter-spacing:.07em; text-transform:uppercase; margin-bottom:5px;
    }

    /* ── Spinner ── */
    .cp-spinner {
      width:14px; height:14px; border-radius:50%;
      border:2px solid rgba(255,255,255,.3); border-top-color:#fff;
      animation:cp-spin .7s linear infinite; display:inline-block; flex-shrink:0;
    }
  `}</style>
);

/* ─────────────────────────────────────────────
   LOCK ICON
───────────────────────────────────────────── */
const LockIcon = ({ size = 20, color = '#6cbb23' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    xmlns="http://www.w3.org/2000/svg" style={{ display:'block', flexShrink:0 }}>
    <rect x="3" y="11" width="18" height="12" rx="3" fill={color} opacity=".15"/>
    <rect x="3" y="11" width="18" height="12" rx="3" stroke={color} strokeWidth="1.8"/>
    <path d="M8 11V7a4 4 0 018 0v4" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="12" cy="16" r="1.5" fill={color}/>
    <path d="M12 17.5v1.5" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

/* ─────────────────────────────────────────────
   PASSWORD STRENGTH HELPER
───────────────────────────────────────────── */
const getStrength = (pwd) => {
  if (!pwd) return { score:0, label:'', color:'#f0f0ec', width:'0%' };
  let s = 0;
  if (pwd.length >= 6)  s++;
  if (pwd.length >= 10) s++;
  if (/[A-Z]/.test(pwd)) s++;
  if (/[0-9]/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  if (s <= 1) return { score:s, label:'Weak',   color:'#ef4444', width:'25%' };
  if (s <= 2) return { score:s, label:'Fair',   color:'#f59e0b', width:'50%' };
  if (s <= 3) return { score:s, label:'Good',   color:'#6cbb23', width:'75%' };
  return             { score:s, label:'Strong', color:'#16a34a', width:'100%' };
};

/* ─────────────────────────────────────────────
   PASSWORD FIELD — shared sub-component
───────────────────────────────────────────── */
const PwdField = ({ label, value, onChange, show, onToggle, hint, showStrength }) => {
  const strength = showStrength ? getStrength(value) : null;
  const match    = null; // strength only for new password field

  return (
    <div style={{ marginBottom:14 }}>
      <label className="cp-lbl">{label}</label>
      <div style={{ position:'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          className="cp-input"
          required
          placeholder="••••••••"
        />
        <button type="button" className="cp-eye" onClick={onToggle} aria-label="Toggle visibility">
          {show ? <FaEye size={14}/> : <FaEyeSlash size={14}/>}
        </button>
      </div>
      {/* Strength bar */}
      {showStrength && value && (
        <div>
          <div className="cp-bar-track">
            <div className="cp-bar-fill" style={{ width:strength.width, background:strength.color }}/>
          </div>
          <div style={{ fontSize:10, color:strength.color, marginTop:4,
            fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:600 }}>
            {strength.label}
          </div>
        </div>
      )}
      {hint && !value && (
        <div style={{ fontSize:11, color:'#c0c0ba', marginTop:5,
          fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{hint}</div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT — LOGIC UNCHANGED
───────────────────────────────────────────── */
const ChangePasswordModal = ({ isOpen, onClose, password, email }) => {
  const [oldPassword,         setOldPassword]         = useState('');
  const [newPassword,         setNewPassword]         = useState('');
  const [confirmPassword,     setConfirmPassword]     = useState('');
  const [showOldPassword,     setShowOldPassword]     = useState(false);
  const [showNewPassword,     setShowNewPassword]     = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading,             setLoading]             = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword?.length < 6) {
      toast.error('Password length must be more than 6');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Password should be equal to confirm password!');
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(
        'https://us-central1-zafflostarter.cloudfunctions.net/changeUserPassword',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, currentPassword: oldPassword, newPassword }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.error || 'Failed to update password');
        return;
      }

      toast.success('Password updated successfully!');
      trackEvent("password_changed", {user_type: "owner"});
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Something went wrong, please try again later');
    } finally {
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setLoading(false);
      setTimeout(() => { onClose(); }, 1000);
    }
  };

  if (!isOpen) return null;

  /* match indicator for confirm field */
  const confirmMatch = confirmPassword.length > 0 && confirmPassword === newPassword;
  const confirmMiss  = confirmPassword.length > 0 && confirmPassword !== newPassword;

  return (
    <>
      <Styles />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {/* Backdrop */}
      <div
        className="cp-bd-anim cp-root"
        onClick={onClose}
        style={{
          position:'fixed', inset:0, zIndex:50,
          background:'rgba(8,12,8,.72)',
          backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
          display:'flex', alignItems:'center', justifyContent:'center',
          padding:16,
        }}
      >
        {/* Card */}
        <div
          className="cp-card-anim"
          onClick={e => e.stopPropagation()}
          style={{
            width:'100%', maxWidth:420,
            background:'#fff', borderRadius:22, overflow:'hidden',
            boxShadow:'0 32px 72px rgba(0,0,0,.3), 0 4px 14px rgba(0,0,0,.1)',
          }}
        >

          {/* ── DARK HEADER ── */}
          <div style={{
            background:'linear-gradient(160deg,#0c1a0c 0%,#152815 60%,#0a120a 100%)',
            padding:'22px 24px 22px',
            position:'relative', overflow:'hidden', minHeight:128,
          }}>
            <div className="cp-hbg"/>
            <div className="cp-hglow"/>
            <div className="cp-cx" style={{ top:12, left:18 }}/>
            <div className="cp-cx" style={{ bottom:12, right:18 }}/>

            {/* Close — z-index:20 above all decoration */}
            <button className="cp-close" onClick={onClose} type="button" aria-label="Close">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 1l8 8M9 1L1 9" stroke="rgba(255,255,255,.75)" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
            </button>

            {/* Header content */}
            <div style={{ position:'relative', zIndex:2 }}>
              <div style={{ display:'flex', alignItems:'center', gap:11, marginBottom:14 }}>
                {/* Icon chip */}
                <div style={{
                  width:40, height:40, borderRadius:12, flexShrink:0,
                  background:'rgba(108,187,35,.18)', border:'1.5px solid rgba(108,187,35,.3)',
                  display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden',
                }}>
                  <LockIcon size={20} color="#6cbb23"/>
                </div>
                <div>
                  <div style={{
                    fontSize:10, fontWeight:700, color:'rgba(108,187,35,.85)',
                    letterSpacing:'.1em', textTransform:'uppercase',
                    fontFamily:"'Plus Jakarta Sans',sans-serif", marginBottom:3,
                  }}>Account Security</div>
                  <h2 className="cp-zh" style={{
                    fontSize:'clamp(17px,2.2vw,21px)', fontWeight:900, fontStyle:'italic',
                    color:'#fff', lineHeight:1.1, letterSpacing:'-0.02em', margin:0,
                  }}>
                    Change Password
                  </h2>
                </div>
              </div>
              <p style={{
                fontSize:12, color:'rgba(255,255,255,.38)', fontWeight:300,
                lineHeight:1.6, margin:0, maxWidth:320,
              }}>
                Choose a strong password to keep your Zafflo account secure.
              </p>
            </div>
          </div>

          {/* ── FORM ── */}
          <form onSubmit={handleChangePassword} style={{ padding:'22px 24px 24px' }}>

            {/* Current password */}
            <PwdField
              label="Current Password"
              value={oldPassword}
              onChange={e => setOldPassword(e.target.value)}
              show={showOldPassword}
              onToggle={() => setShowOldPassword(s => !s)}
            />

            {/* New password + strength */}
            <PwdField
              label="New Password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              show={showNewPassword}
              onToggle={() => setShowNewPassword(s => !s)}
              hint="Minimum 6 characters · Mix letters, numbers & symbols"
              showStrength
            />

            {/* Confirm password */}
            <div style={{ marginBottom:18 }}>
              <label className="cp-lbl">Confirm New Password</label>
              <div style={{ position:'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="cp-input"
                  required
                  placeholder="••••••••"
                  style={{
                    borderColor: confirmMatch ? '#6cbb23' : confirmMiss ? '#ef4444' : undefined,
                    boxShadow:   confirmMatch ? '0 0 0 3px rgba(108,187,35,.1)'
                               : confirmMiss  ? '0 0 0 3px rgba(239,68,68,.08)'
                               : undefined,
                  }}
                />
                <button type="button" className="cp-eye" onClick={() => setShowConfirmPassword(s => !s)} aria-label="Toggle">
                  {showConfirmPassword ? <FaEye size={14}/> : <FaEyeSlash size={14}/>}
                </button>
              </div>
              {/* Match status */}
              {confirmPassword.length > 0 && (
                <div style={{
                  display:'flex', alignItems:'center', gap:5, marginTop:5,
                  fontSize:11, fontFamily:"'Plus Jakarta Sans',sans-serif",
                  color: confirmMatch ? '#6cbb23' : '#ef4444', fontWeight:600,
                }}>
                  {confirmMatch ? (
                    <>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M1.5 5l2.5 2.5L8.5 2" stroke="#6cbb23" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Passwords match
                    </>
                  ) : (
                    <>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 2l6 6M8 2L2 8" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/>
                      </svg>
                      Passwords don't match
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Security tip */}
            <div style={{
              display:'flex', alignItems:'flex-start', gap:10,
              padding:'10px 13px', marginBottom:18,
              background:'#f8fdf4', border:'1.5px solid #cdeaa0', borderRadius:11,
            }}>
              <div style={{
                width:22, height:22, borderRadius:6, flexShrink:0, marginTop:1,
                background:'rgba(108,187,35,.15)', border:'1px solid rgba(108,187,35,.28)',
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
                <LockIcon size={11} color="#6cbb23"/>
              </div>
              <p style={{
                fontSize:11.5, color:'#5a8c1a', lineHeight:1.55, margin:0,
                fontFamily:"'Plus Jakarta Sans',sans-serif",
              }}>
                Use a mix of uppercase, lowercase, numbers and symbols for a strong password.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="cp-submit"
            >
              {loading ? (
                <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  <span className="cp-spinner"/>
                  Updating…
                </span>
              ) : (
                <span>Update Password</span>
              )}
            </button>

            {/* Cancel */}
            <button type="button" onClick={onClose} className="cp-cancel">
              Cancel
            </button>

          </form>
        </div>
      </div>
    </>
  );
};

export default ChangePasswordModal;